if (!customElements.get('nosht-announcement-marquee')) {
class NoshtAnnouncementMarquee extends HTMLElement {
  constructor() {
    super();
    this.userPaused = false;
    this.offset = 0;
    this.period = 0;
    this.lifeVersion = 0;
    this.tick = this.tick.bind(this);
  }

  connectedCallback() {
    this.releaseResources();
    const version = ++this.lifeVersion;
    this.viewport = this.querySelector('[data-marquee-viewport]');
    this.track = this.querySelector('[data-marquee-track]');
    this.source = this.querySelector('[data-marquee-source]');
    this.toggle = this.querySelector('[data-marquee-toggle]');
    if (!this.viewport || !this.track || !this.source) return;

    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.hovered = false;
    this.sourceFocused = this.source.contains(document.activeElement);
    this.editorSelected = false;
    this.intersects = true;
    this.ready = false;
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onMotionChange = () => this.updateState();
    this.motionQuery.addEventListener('change', this.onMotionChange);

    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'touch') return;
      this.hovered = true;
      this.updateState();
    }, options);
    this.addEventListener('pointerleave', () => {
      this.hovered = false;
      this.updateState();
    }, options);
    this.source.addEventListener('focusin', () => {
      this.sourceFocused = true;
      this.updateState();
    }, options);
    this.source.addEventListener('focusout', () => {
      // Focus may move to another link in the same source sequence.
      queueMicrotask(() => {
        if (version !== this.lifeVersion || !this.isConnected) return;
        this.sourceFocused = this.source.contains(document.activeElement);
        this.updateState();
      });
    }, options);
    this.toggle?.addEventListener('click', () => {
      this.userPaused = !this.userPaused;
      this.updateState();
    }, options);
    document.addEventListener('visibilitychange', () => this.updateState(), options);
    document.addEventListener('shopify:block:select', (event) => {
      if (!this.ownsEditorBlock(event)) return;
      this.editorSelected = true;
      this.updateState();
    }, options);
    document.addEventListener('shopify:block:deselect', (event) => {
      if (!this.ownsEditorBlock(event)) return;
      this.editorSelected = false;
      this.updateState();
    }, options);

    this.resizeObserver = new ResizeObserver(() => this.queueMeasure());
    this.resizeObserver.observe(this.viewport);
    this.resizeObserver.observe(this.source);
    this.mutationObserver = new MutationObserver(() => this.queueMeasure());
    this.mutationObserver.observe(this.source, { childList: true, subtree: true, characterData: true });
    if (typeof IntersectionObserver === 'function') {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        this.intersects = entries[0]?.isIntersecting !== false;
        this.updateState();
      });
      this.intersectionObserver.observe(this);
    }
    document.fonts?.addEventListener('loadingdone', () => this.queueMeasure(), options);
    document.fonts?.ready.then(() => {
      if (version === this.lifeVersion && this.isConnected) this.queueMeasure();
    });
    this.queueMeasure();
    this.updateState();
  }

  disconnectedCallback() {
    ++this.lifeVersion;
    this.releaseResources();
    this.removeClones();
    this.removeAttribute('data-marquee-ready');
    this.removeAttribute('data-marquee-static');
    this.style.removeProperty('--nosht-marquee-offset');
    this.style.removeProperty('--nosht-marquee-period');
    if (this.toggle) this.toggle.hidden = true;
  }

  releaseResources() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.motionQuery?.removeEventListener('change', this.onMotionChange);
    cancelAnimationFrame(this.frame);
    cancelAnimationFrame(this.measureFrame);
    this.frame = null;
    this.measureFrame = null;
    this.lastTime = null;
  }

  ownsEditorBlock(event) {
    if (event.target instanceof Element && this.source.contains(event.target)) return true;
    const blockId = event.detail?.blockId;
    return blockId != null && Array.from(this.source.querySelectorAll('[data-announcement-id]'))
      .some((item) => item.dataset.announcementId === String(blockId));
  }

  queueMeasure() {
    if (this.measureFrame != null || !this.isConnected) return;
    this.measureFrame = requestAnimationFrame(() => {
      this.measureFrame = null;
      this.measure();
    });
  }

  removeClones() {
    this.track?.querySelectorAll('[data-marquee-clone]').forEach((clone) => clone.remove());
  }

  createClone() {
    const clone = this.source.cloneNode(true);
    clone.removeAttribute('data-marquee-source');
    clone.setAttribute('data-marquee-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    // Repeated links remain usable with a pointer, but occur only once in Tab
    // order and the accessibility tree. Never duplicate Theme Editor targets.
    [clone, ...clone.querySelectorAll('*')].forEach((node) => {
      node.removeAttribute('id');
      Array.from(node.attributes).forEach(({ name }) => {
        if (name.startsWith('data-shopify-editor-')) node.removeAttribute(name);
      });
      if (node.matches('a, button, input, select, textarea, summary, [tabindex], [contenteditable]')) {
        node.setAttribute('tabindex', '-1');
      }
    });
    return clone;
  }

  measure() {
    if (!this.isConnected || !this.source) return;
    this.stop();
    // Measure the intrinsic sequence even when reduced motion/focus currently
    // wraps its text. Restore the static state synchronously before painting.
    this.setAttribute('data-marquee-ready', '');
    this.removeAttribute('data-marquee-static');
    const width = this.source.getBoundingClientRect().width;
    const viewportWidth = this.viewport.clientWidth;
    const oldPeriod = this.period;
    this.removeClones();
    this.ready = Number.isFinite(width) && width > 0 && viewportWidth > 0;
    if (this.ready) {
      this.period = width;
      this.offset = oldPeriod > 0 ? (this.offset / oldPeriod % 1) * width : 0;
      const copies = Math.ceil(viewportWidth / width) + 1;
      const fragment = document.createDocumentFragment();
      for (let i = 0; i < copies; i += 1) fragment.append(this.createClone());
      this.track.append(fragment);
      this.style.setProperty('--nosht-marquee-period', `${width}px`);
      this.paint();
    }
    this.toggleAttribute('data-marquee-ready', this.ready);
    this.updateState();
  }

  isStatic() {
    return this.motionQuery?.matches || this.sourceFocused || this.editorSelected;
  }

  canRun() {
    return this.ready && this.isConnected && !this.isStatic()
      && !this.userPaused && !this.hovered && !document.hidden && this.intersects;
  }

  updateState() {
    const isStatic = Boolean(this.isStatic());
    this.toggleAttribute('data-marquee-static', isStatic);
    if (this.toggle) {
      this.toggle.hidden = !this.ready || this.motionQuery?.matches;
      const label = this.userPaused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel;
      if (label) {
        this.toggle.setAttribute('aria-label', label);
        this.toggle.setAttribute('title', label);
      }
      this.toggle.setAttribute('aria-pressed', String(this.userPaused));
      this.toggle.querySelectorAll('[data-marquee-icon]').forEach((icon) => {
        icon.toggleAttribute('hidden', icon.dataset.marqueeIcon !== (this.userPaused ? 'play' : 'pause'));
      });
    }
    if (!this.canRun()) {
      this.stop();
      return;
    }
    if (this.frame == null) this.frame = requestAnimationFrame(this.tick);
  }

  stop() {
    cancelAnimationFrame(this.frame);
    this.frame = null;
    this.lastTime = null;
  }

  paint() {
    // CSS consumes this single distance for BOTH text and gradient positions.
    this.style.setProperty('--nosht-marquee-offset', `${-this.offset}px`);
  }

  tick(time) {
    this.frame = null;
    if (!this.canRun()) {
      this.lastTime = null;
      return;
    }
    if (this.lastTime != null) {
      const elapsed = Math.min(Math.max(time - this.lastTime, 0), 100);
      const configuredSpeed = Number(this.dataset.speed);
      const speed = Number.isFinite(configuredSpeed) && configuredSpeed > 0 ? configuredSpeed : 35;
      this.offset = (this.offset + elapsed * speed / 1000) % this.period;
      this.paint();
    }
    this.lastTime = time;
    this.frame = requestAnimationFrame(this.tick);
  }
}

  customElements.define('nosht-announcement-marquee', NoshtAnnouncementMarquee);
}
